import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';

import { DatePipe } from '@angular/common';
import {
  DetalhesProcessamentoCarteiraFilter,
  ExecutarProcessamento,
  ProcessamentoFilter,
  ProcessamentoPeriodoFilter,
  ProcessamentoPeriodoModel,
} from './processamento/executar-processamento';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PaginationResponse } from '../shared/form/pagination-response';
import { ProcessamentoPesquisaModel } from './processamento/processamento-pesquisa-model';
import { catchError } from 'rxjs/operators';
import { handleError } from '../shared/util/error-util';
import { CarteiraAutocompleteMapper } from '../shared/components/carteira-autocomplete/carteira-autocomplete.mapper';

@Injectable({
  providedIn: "root",
})
export class ProcessamentoService {
  url = "/v1/processamentos";

  constructor(
    private readonly datePipe: DatePipe,
    private readonly httpClient: HttpClient,
    private readonly carteiraAutocompleteMapper: CarteiraAutocompleteMapper
  ) {
  }

  private readonly _httpHeaders = new HttpHeaders({
    "Content-Type": "application/json",
  });

  private readonly dateFormat = "yyyy-MM-dd";

  search(value: ProcessamentoPesquisaModel, pageSize: number = 20): Observable<PaginationResponse> {
    this.carteiraAutocompleteMapper.map(value);
    const headers = this._httpHeaders;
    let params: HttpParams = new HttpParams();

    Object.keys(value).forEach((item) => {
      if (this.notNullNotEmpty(value[item]))
        params = params.set(item, value[item]);
    });

    if (value["dataUltimoProcessamento"]) {
      params = params.set(
        "dataUltimoProcessamento",
        this.datePipe.transform(value["dataUltimoProcessamento"], this.dateFormat)
      );
    }

    params = params.set("size", pageSize + "");
    params = params.set("sort", "nome,ASC");
    const options = {headers, params};

    return this.httpClient.get<any>(`${this.url}`, options);
  }

  executarProcessamentoPeriodoSelecionadas(carteiras: number[], dataInicio: string, dataFim: string): Observable<PaginationResponse> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = { headers, params };

    const body = new ProcessamentoPeriodoModel(
      carteiras,
      this.datePipe.transform(dataInicio, this.dateFormat),
      this.datePipe.transform(dataFim, this.dateFormat)
    );

    return this.httpClient.post<any>(`${this.url}/periodo`, body, options);
  }

  executarProcessamentoPeriodoTodas(formFilter: any, dataInicio: string, dataFim: string): Observable<PaginationResponse> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = { headers, params };

    const dataUltimoProcessamento = formFilter.dataUltimoProcessamento
      ? this.datePipe.transform(formFilter.dataUltimoProcessamento,this.dateFormat)
      : null;

    const filtro = new DetalhesProcessamentoCarteiraFilter(formFilter.carteiraId, formFilter.grupoCarteiraId, dataUltimoProcessamento, formFilter.status);
    const body = new ProcessamentoPeriodoFilter(
      filtro,
      this.datePipe.transform(dataInicio, this.dateFormat),
      this.datePipe.transform(dataFim, this.dateFormat)
    );

    return this.httpClient.post<any>(`${this.url}/periodo/filter`, body, options);
  }

  cancelarProcessamento(carteiras: number[]): Observable<PaginationResponse> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = { headers, params };

    return this.httpClient.post<any>(`${this.url}/cancelar`, carteiras, options);
  }

  cancelarProcessamentoTodas(formFilter: any): Observable<PaginationResponse> {
    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = { headers, params };

    const dataUltimoProcessamento = formFilter.dataUltimoProcessamento
      ? this.datePipe.transform(formFilter.dataUltimoProcessamento,this.dateFormat)
      : null;

    const body = new DetalhesProcessamentoCarteiraFilter(formFilter.carteiraId, formFilter.grupoCarteiraId, dataUltimoProcessamento, formFilter.status);

    return this.httpClient.post<any>(`${this.url}/cancelar/filter`, body, options);
  }

  executarProcessamentoFiltradas(value: any): Observable<PaginationResponse> {
    if (value["dataUltimoProcessamento"]) {
      value["dataUltimoProcessamento"] = this.datePipe.transform(
        value["dataUltimoProcessamento"],
        this.dateFormat
      );
    }
    if (value["dataProcessamento"]) {
      value["dataProcessamento"] = this.datePipe.transform(
        value["dataProcessamento"],
        this.dateFormat
      );
    }

    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = { headers, params };

    const filtro = new DetalhesProcessamentoCarteiraFilter(value.carteiraId, value.grupoCarteiraId, value.dataUltimoProcessamento, value.status);
    const model = new ProcessamentoFilter(filtro, value.dataProcessamento);

    return this.httpClient.post<any>(`${this.url}/filter`, model, options);
  }

  executarProcessamento(value, carteiras): Observable<PaginationResponse> {
    if (value["dataEnquadramento"]) {
      value["dataEnquadramento"] = this.datePipe.transform(
        value["dataEnquadramento"],
        this.dateFormat
      );
    }

    if (value["dataProcessar"]) {
      value["dataProcessar"] = this.datePipe.transform(
        value["dataProcessar"],
        this.dateFormat
      );
    }

    const headers = this._httpHeaders;
    const params: HttpParams = new HttpParams();
    const options = {headers, params};

    const model = Object.assign(new ExecutarProcessamento(), value);
    model.carteiras = carteiras;

    return this.httpClient.post<any>(
      `${this.url}`,
      model,
      options
    );
  }

  notNullNotEmpty(value: any): boolean {
    return value !== null && value !== "" && value !== undefined;
  }

  private createFormParam(value: any) {

    let params: HttpParams = new HttpParams();

    Object.keys(value).forEach((item) => {
      if (this.notNullNotEmpty(value[item]))
        params = params.set(item, value[item]);
    });

    if (value["dataUltimoProcessamento"]) {
      params = params.set(
        "dataUltimoProcessamento",
        this.datePipe.transform(value["dataUltimoProcessamento"], this.dateFormat)
      );
    }

    params = params.set("sort", "nome,ASC");

    return params;
  }

  exportPdf(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpClient.get(`${this.url}/export/pdf`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }

  exportXlsx(value: any): Observable<HttpResponse<Blob>> {
    const headers = this._httpHeaders;
    const params = this.createFormParam(value).set("size", '99999999');
    return this.httpClient.get(`${this.url}/export/xlsx`, {
      headers,
      responseType: "blob",
      observe: "response",
      params
    }).pipe(catchError(handleError));
  }
}
